import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QUOTE_FILIATION } from "v4/data/apiRoutes";
import { useNavigation } from "v4/features/front/navigation/contexts/NavigationContext";
import { resolveUrl } from "v4/features/front/navigation/utils/route.utils";
import useFetch from "v4/hooks/useFetch";
import { generateUrl } from "v4/services/api.service";

let cacheFiliation = {
    ids: [],
    data: {},
};

export default function useProspectQuotesTabs() {
    const { t } = useTranslation();
    const [{ data }, fetchFiliation] = useFetch();
    const { currentRoute } = useNavigation();
    const { prospect: prospectId, quote: quoteId } = currentRoute.params;

    const [filiation, setFiliation] = useState(() => {
        if (cacheFiliation.ids.includes(quoteId)) return cacheFiliation;

        return { ids: [], data: {} }
    });

    useEffect(() => {
        if (!quoteId || filiation.ids.includes(quoteId)) return;

        fetchFiliation({ url: generateUrl(QUOTE_FILIATION, { id: quoteId }), })
    }, [fetchFiliation, filiation.ids, quoteId]);

    useEffect(() => {
        if (!data) return;

        setFiliation({
            ids: [data.parent.id, ...data.children.map(({ id }) => id)],
            data,
        });
    }, [data]);

    // adding cache to avoid re-fetching data when switching between tabs
    useEffect(() => {
        if (filiation.ids.length === 0) return;

        return () => {
            cacheFiliation = filiation;
        }
    }, [filiation]);

    const { parent = null, children = [] } = filiation.data;

    return useMemo(() => {
        const getUrl = (quoteId) => {
            if (!currentRoute?.route?.url) return '#';

            return `/${resolveUrl(currentRoute.route.url, { prospect: prospectId })}/${quoteId}`;
        }

        const parentToShow = parent && {
            ...parent,
            isCurrent: parent.id === quoteId,
            url: getUrl(parent.id),
            label: t('main_tab', {tab: currentRoute.route.label}),
        };

        const childrenToShow = children && children?.map(child => ({
            ...child,
            isCurrent: child.id === quoteId,
            url: getUrl(child.id),
            label: parentToShow ? clampParentNameForCurrentTabName(parentToShow.name, child.name) : child.name,
        }));

        return {
            parent: parentToShow,
            children: childrenToShow,
        };
    }, [parent, quoteId, currentRoute, t, children, prospectId]);
}

function clampParentNameForCurrentTabName(parentName, currentTabName) {
    if (parentName) {
        return currentTabName.replace(`${parentName} - `, '');
    }

    return currentTabName;
}
